<template>
  <section class="timeline-container">
    <div class="timeline-wrapper">
      <!-- Order Selection -->
      <div class="order-selector">
        <div>{{ dataLabel }}</div>
        <el-radio-group
          v-model="sortOrder"
          @change="handleOrderChange"
          size="mini"
        >
          <el-radio-button label="desc">Newest First</el-radio-button>
          <el-radio-button label="asc">Oldest First</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Skeleton Loading -->
      <el-timeline v-if="loading" class="custom-timeline">
        <el-timeline-item
          v-for="i in 3"
          :key="`skeleton-${i}`"
          class="timeline-item"
        >
          <el-skeleton animated>
            <template #template>
              <div class="timeline-content">
                <el-skeleton-item variant="text" style="width: 200px" />
                <ul class="timeline-list">
                  <li
                    v-for="j in 3"
                    :key="`skeleton-item-${j}`"
                    class="timeline-list-item"
                  >
                    <el-skeleton-item variant="text" style="width: 90%" />
                  </li>
                </ul>
                <el-skeleton-item variant="text" style="width: 150px" />
              </div>
            </template>
          </el-skeleton>
        </el-timeline-item>
      </el-timeline>

      <!-- Actual Timeline -->
      <el-timeline v-else class="custom-timeline">
        <el-timeline-item
          v-for="activity in getParsedData"
          :key="`timeline-item-${activity.created_at}`"
          :icon="getIcon(activity)"
          :color="getColor(activity)"
          size="large"
          placement="top"
          :timestamp="activity.timestamp"
          :hollow="activity.hollow"
          class="timeline-item"
        >
          <div class="timeline-content">
            <ul v-if="activity.content?.length" class="timeline-list">
              <li
                v-for="(item, itemIndex) in activity.content"
                :key="`content-item-${itemIndex}`"
                class="timeline-list-item"
              >
                <p v-html="item"></p>
              </li>
            </ul>
            <ul v-else class="timeline-list">
              <li class="timeline-list-item">
                <p>Updated with no changes.</p>
              </li>
            </ul>
            <div v-if="activity.activity_by" class="timeline-footer">
              By {{ activity.activity_by }}
              <span
                v-if="activity.action_from != 'Entity' && !activity.is_create"
              >
                - {{ activity.action_from }}
              </span>
            </div>
          </div>
        </el-timeline-item>
      </el-timeline>
    </div>
  </section>
</template>

<script>
//   import moment from "moment";
// import { getTableDiffSummary } from "../helpers/appHelper";
import { postAPICall } from "../helpers/httpHelper";
import FieldsFormatHelper from "../mixins/FieldsFormatHelper";

export default {
  name: "AppTimeline",
  props: {
    fieldsObject: {
      type: Object,
      default: () => ({}),
    },
    createdAtData: {
      type: Object,
      required: true,
    },
    dataLabel: {
      type: String,
    },
  },
  mixins: [FieldsFormatHelper],
  data() {
    return {
      loading: true,
      currentLogs: [],
      contactsObject: {},
      dataId: null,
      parsedData: [],
      sortOrder: "desc", // default to descending (newest first)
      colors: ["#67C23A", "#409EFF", "#E6A23C", "#F56C6C", "#909399"],
    };
  },
  mounted() {
    this.dataId = this.createdAtData._id;
    this.createdAtData = {
      ...(this.createdAtData || {}),
      action: "Created",
    };
    this.fetchCurrentLogs();
  },
  methods: {
    async fetchCurrentLogs() {
      try {
        this.loading = true;
        const logsResponse = await postAPICall(
          "GET",
          `/audit/fetch-logs?dataId=${this.dataId}`
        );

        this.currentLogs = logsResponse?.data || [];
        //you can delete it if you want to show not updated data as well
        await this.fetchUsersInfo();
        this.transformData();
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        this.loading = false;
      }
    },
    async fetchUsersInfo() {
      try {
        const user_ids = new Set([this.createdAtData.action_by]);
        // const contact_ids = new Set([this.createdAtData.action_by]);

        this.currentLogs.forEach((e) => {
          // contact_ids.add(e.action_by);
          user_ids.add(e.action_by);
        });

        const response = await postAPICall(
          "POST",
          "/entities-data/users-info",
          {
            contact_ids: [],
            user_ids: Array.from(user_ids),
          }
        );

        this.contactsObject = response?.data || {};
      } catch (e) {
        console.error("Error fetching user info:", e);
      }
    },
    transformData() {
      const combinedData = this.currentLogs.some((e) => e.action == "Created")
        ? this.currentLogs
        : [this.createdAtData, ...this.currentLogs];

      this.parsedData = combinedData.map((item) => {
        const data = {
          ...(item || {}),
          created_at: item.created_at,
          timestamp: this.$options.filters.getFormattedDateTypeThree(
            item.created_at
          ),
          activity_by: this.getActivityBy(item),
          action: item.action,
          action_from: item.action_from,
        };
        data.content = this.getContent(data, this.fieldsObject);
        return data;
      });

      this.sortData();
    },
    handleOrderChange() {
      this.sortData();
    },
    sortData() {
      this.parsedData = this.parsedData.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return this.sortOrder === "desc" ? dateB - dateA : dateA - dateB;
      });
    },
    getActivityBy(data) {
      const contact = this.contactsObject[data.action_by];
      if (!contact) return "-";

      return `${contact.first_name} ${contact.last_name}${
        data.is_contact ? " (AU)" : ""
      }`;
    },
    // prepareContent(forms) {
    //   const { from, to } = forms;
    //   const emptyValues = ["-", null, undefined];

    //   return Object.keys(to)
    //     .filter((key) => this.fieldsObject[key])
    //     .map((key) => {
    //       const field = this.fieldsObject[key];
    //       const value = this.getFormattedFieldData(field, from, true);
    //       const value2 = this.getFormattedFieldData(field, to, true);

    //       // Common template parts
    //       const label = `<b>${field.label}</b>`;
    //       const defaultChange = `${label} changed from <i>${
    //         emptyValues.includes(value) ? "N/A" : value
    //       }</i> to <i>${value2}</i>.`;

    //       switch (field.inputType) {
    //         case "IMAGE":
    //         case "VIDEO":
    //         case "FILE":
    //         case "SIGNATURE": {
    //           const isNew = !emptyValues.includes(value);
    //           const action =
    //             field.inputType === "SIGNATURE" ? "signed" : "uploaded";
    //           return isNew
    //             ? `New ${label} has been ${action}.`
    //             : `${label} has been updated.`;
    //         }
    //         case "ENTITY_TABLE": {
    //           const diff = value?.length - value2?.length;
    //           let tableChange;
    //           if (diff > 0) {
    //             tableChange = `- ${diff} row(s) removed.`;
    //           } else if (diff < 0) {
    //             tableChange = `- ${Math.abs(diff)} row(s) added.`;
    //           } else {
    //             tableChange = `- some row(s) modified.`;
    //           }
    //           return `<p>${label}:</p>&nbsp; &nbsp;${tableChange}`;
    //         }
    //         case "DATA_TABLE":
    //           return `${label}:&nbsp; &nbsp;${getTableDiffSummary(
    //             value || [],
    //             value2 || [],
    //             "keyIndex",
    //             field.data_table_columns
    //           )}`;
    //         default:
    //           return defaultChange;
    //       }
    //     });
    // },
    getColor(activity) {
      console.log("activity", activity);
      if (activity.action_from == "Automation") {
        return "#ca65d1";
      }
      switch (activity.action) {
        case "Updated":
          return "#E6A23C";
        case "Send Notification":
          return "#c9ae32";
        case "Deleted":
          return "#D11A2A";
        case "Created":
          return "#64B5F6";
        default:
          return "#64B5F6";
      }
    },
    getIcon(activity) {
      if (activity.action_from == "Automation") {
        return "el-icon-s-tools";
      }
      switch (activity.action) {
        case "Updated":
          return "el-icon-edit-outline";
        case "Deleted":
          return "el-icon-delete";
        case "Created":
          return "el-icon-document-add";
        default:
          return "el-icon-document-add";
      }
    },
  },
  computed: {
    getParsedData() {
      return this.parsedData;
    },
  },
};
</script>

<style scoped>
.timeline-container {
  padding: 1.2rem;
  background-color: #f5f7fa;
}

.timeline-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.order-selector {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  > div:first-child {
    font-weight: 500;
    color: #334155;
  }
}

.custom-timeline {
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.timeline-item {
  padding-bottom: 1.5rem;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-list {
  margin: 0.5rem 0;
  padding-left: 1.2rem;
  color: #606266;
}

.timeline-list-item {
  margin-bottom: 0.3rem;
  line-height: 1.5;
}

.timeline-list-item:last-child {
  margin-bottom: 0;
}

.timeline-footer {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #909399;
  font-style: italic;
}

@media (max-width: 768px) {
  .timeline-container {
    padding: 1rem;
  }

  .timeline-wrapper {
    padding: 0 10px;
    width: 100%;
  }

  .order-selector {
    justify-content: center;
  }
}

.custom-timeline ::v-deep .el-timeline-item__node {
  width: 25px !important;
  height: 25px !important;
  left: -9px !important;
  top: 5% !important;
  transform: translateY(-50%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.custom-timeline ::v-deep .el-timeline-item__icon {
  font-size: 18px !important;
}

.custom-timeline ::v-deep .el-timeline-item__node.is-hollow {
  border-width: 3px !important;
}

.custom-timeline ::v-deep .el-timeline-item__tail {
  top: 24px !important;
  bottom: 0 !important;
}
</style>
